import moment from 'moment';
import { BookingMode } from '../../enums/booking-mode.enum';
import { ReservationMode } from '../../enums/reservation-mode.enum';

/**
 * A month is 30 nights here, the same month the pricing and min-stay code use
 * (`price * 30`, `minStay / 30`).
 */
export const NIGHTS_IN_A_MONTH = 30;

/**
 * Daily or monthly, by length of stay: anything shorter than a month is daily,
 * a month or longer is monthly.
 *
 * Counts nights from the dates rather than trusting `numberOfNights`, which the
 * details payload does not carry. Both ends are taken to the start of their day so
 * a check-in at 15:00 and a check-out at 12:00 still count whole nights.
 */
export const getStayType = (
  checkInDate?: number,
  checkOutDate?: number
): ReservationMode | undefined => {
  if (!checkInDate || !checkOutDate) return undefined;

  const nights = moment(checkOutDate)
    .startOf('day')
    .diff(moment(checkInDate).startOf('day'), 'days');
  if (nights <= 0) return undefined;

  return nights < NIGHTS_IN_A_MONTH ? ReservationMode.DAILY : ReservationMode.MONTHLY;
};

const stayTypeLabels: Record<ReservationMode, string> = {
  [ReservationMode.DAILY]: 'Daily',
  [ReservationMode.MONTHLY]: 'Monthly'
};

const stayTypeDescriptions: Record<ReservationMode, string> = {
  [ReservationMode.DAILY]: `Daily stay (under ${NIGHTS_IN_A_MONTH} nights)`,
  [ReservationMode.MONTHLY]: `Monthly stay (${NIGHTS_IN_A_MONTH}+ nights)`
};

const bookingModeLabels: Record<BookingMode, string> = {
  [BookingMode.INSTANT]: 'Instant',
  [BookingMode.REQUEST]: 'Request'
};

/**
 * The header tag that says how the reservation was booked and how long it is for —
 * "Instant · Monthly", "Request · Daily". Either half can be missing on its own;
 * undefined when both are.
 */
export const getBookingModeTag = (
  bookingMode?: BookingMode | string,
  checkInDate?: number,
  checkOutDate?: number
): { label: string; tooltip: string; isInstant: boolean } | undefined => {
  const mode = bookingModeLabels[bookingMode as BookingMode];
  const stayType = getStayType(checkInDate, checkOutDate);
  if (!mode && !stayType) return undefined;

  const label = [mode, stayType && stayTypeLabels[stayType]].filter(Boolean).join(' · ');
  const tooltip = [mode && `${mode} Booking`, stayType && stayTypeDescriptions[stayType]]
    .filter(Boolean)
    .join(' · ');
  return { label, tooltip, isInstant: bookingMode === BookingMode.INSTANT };
};
